import { Component, computed, Input, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { TranslateService } from '@ngx-translate/core';
import { noop } from 'underscore';

import { VideoPlaybackComponent } from 'rev-portal/media/videos/videoPlayback/VideoPlayback.Component';
import { VideoPlaybackService } from 'rev-portal/media/videos/videoPlayback/VideoPlayback.Service';
import { IVideoPlaybackConfig } from 'rev-portal/media/videos/videoPlayback/IVideoPlaybackConfig';

import { ConfirmationDialogAngularComponent } from 'rev-shared/ui/dialog/ConfirmationDialogAngular.Component';
import { DialogService } from 'rev-shared/ui/dialog/Dialog.Service';
import { GenerateMetadataStatus } from 'rev-shared/videoAiMetadata/VideoAiMetadataTypes';
import { SecurityContextService } from 'rev-shared/security/SecurityContext.Service';
import { VideoAiMetadataService } from 'rev-shared/videoAiMetadata/VideoAiMetadata.Service';
import { VideoSource } from 'rev-shared/media/VideoSource';
import { isMobile } from 'rev-shared/util/UserAgentUtil';
import { DayMs } from 'rev-shared/date/Time.Constant';
import { LIVE_EVENT_STATUSES } from 'rev-shared/media/Constants';
import { PORTAL_VIDEO_SETTINGS_STATE, PORTAL_VIDEO_EDIT_STATE } from 'rev-shared/bootstrap/AppStateNames';

import menuItemStyles from 'rev-shared/ui/menu-item/menu-item.module.less';

@Component({
	selector: 'video-playback-settings-menu',
	templateUrl: './VideoPlaybackSettingsMenu.Component.html'
})
export class VideoPlaybackSettingsMenuComponent implements OnInit {
	@Input() public mediaFeatures: any;
	@Input() public playbackConfig: IVideoPlaybackConfig;
	@Input() public video: any;

	public readonly PORTAL_VIDEO_SETTINGS_STATE = PORTAL_VIDEO_SETTINGS_STATE;
	public readonly PORTAL_VIDEO_EDIT_STATE = PORTAL_VIDEO_EDIT_STATE;
	public readonly menuItemStyles = menuItemStyles;
	public readonly IGenerateMetadatauttonState = GenerateMetadataStatus;
	public readonly isMobile = isMobile();
	private readonly canEditVideo = toSignal(this.VideoPlaybackSvc.canEditVideo());
	public readonly showEditVideo = computed(() => {
		const canEdit = this.canEditVideo();
		const generatingChapters = this.VideoAiMetadataService.generateChaptersSignal() === GenerateMetadataStatus.PROCESSING;
		return canEdit && !generatingChapters;
	});

	private isAccountAdminUser: boolean;

	constructor(
		private SecurityContext: SecurityContextService,
		private VideoPlaybackSvc: VideoPlaybackService,
		private Dialog: DialogService,
		private TranslateService: TranslateService,
		public VideoAiMetadataService: VideoAiMetadataService,
		public VideoPlaybackComponent: VideoPlaybackComponent
	) { }

	public ngOnInit(): void {
		this.isAccountAdminUser = !!this.SecurityContext.checkAuthorization('admin.accounts.edit');
	}

	private get canManageLegalHold(): boolean {
		return this.isAccountAdminUser && this.mediaFeatures.enableLegalHold;
	}

	public get showApplyLegalHold(): boolean {
		return this.canManageLegalHold && this.video && !this.video.legalHold;
	}

	public get showDeleteVideo(): boolean {
		return this.video?.canEditVideo && !this.video.legalHold && !(LIVE_EVENT_STATUSES.includes(this.video.status) && Date.now() - this.video.whenUploaded.getTime() < DayMs);
	}

	public get showMenu(): boolean {
		const showEdit = this.showEditVideo();

		return this.showApplyLegalHold ||
			this.showDeleteVideo ||
			showEdit ||
			this.showRemoveLegalHold ||
			this.showReplaceVideo ||
			this.showSettings;
	}

	public get showRemoveLegalHold(): boolean {
		return this.canManageLegalHold && this.video?.legalHold;
	}

	public get showPreposition(): boolean {
		return this.mediaFeatures.enableIndividualVideoPreposition &&
			this.showSettings &&
			this.VideoPlaybackComponent.isReadyToPlay &&
			this.video.source !== VideoSource.LINK &&
			this.VideoPlaybackComponent.hasMediaEditAuth;
	}

	public get showReplaceVideo(): boolean {
		return this.showSettings &&
			this.video.source !== VideoSource.LINK &&
			!this.VideoPlaybackSvc.isPreCutoffDualStream(this.video);
	}

	public get showSettings(): boolean {
		return this.video?.canEditVideo && !this.video.legalHold;
	}

	public applyLegalHold(): void {
		this.openDialog(
			'Video_LegalHold_Apply_Dialog_Message',
			'Video_LegalHold'
		).result.then(() => {
			this.VideoPlaybackSvc.setLegalHold(this.video.id, true)
				.catch(e => console.error('Error setting legal hold: ', e));
		}).catch(() => noop);
	}

	public removeLegalHold(): void {
		this.openDialog(
			'Video_LegalHold_Remove_Dialog_Message',
			'Video_LegalHold'
		).result.then(() => {
			this.VideoPlaybackSvc.setLegalHold(this.video.id, false)
				.catch(e => console.error('Error removing legal hold: ', e));
		}).catch(() => noop);
	}

	public deleteVideo(): void {
		this.VideoPlaybackComponent.deleteVideo();
	}

	private openDialog(messageKey: string, titleKey: string, actionKey: string = 'Ok', cancelKey: string = 'Cancel'): ConfirmationDialogAngularComponent {
		return this.Dialog.openConfirmationDialog(
			{
				message: this.TranslateService.instant(messageKey),
				title: this.TranslateService.instant(titleKey),
				actionText: this.TranslateService.instant(actionKey),
				cancelText: this.TranslateService.instant(cancelKey)
			}
		);
	}
}
